<script setup>
// RM-427: one group's application. The group inherits its path's defaults
// (Admin → Applications); here an RM Admin changes Show / Required for this
// group only, question by question, and sees which settings are the group's
// own. Saving touches nothing else on the group.
import { computed, reactive, ref, watch } from 'vue'
import axios from '@axios'
import ApplicationPreviewDialog from '@core/components/application/ApplicationPreviewDialog.vue'
import { STEP_ORDER, STEP_TITLES } from '@/utils/applicationForm'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  groupId: { type: String, default: null },
})

const emit = defineEmits(['update:modelValue'])

const loading = ref(false)
const saving = ref(false)
const error = ref('')
const settings = ref(null)
// key → { show: null|true|false, required: null|true|false } (null = inherit)
const overrides = reactive({})
const savedMessage = ref('')

const CHOICES = [
  { title: 'Inherit', value: null },
  { title: 'Yes', value: true },
  { title: 'No', value: false },
]

const resetOverrides = list => {
  Object.keys(overrides).forEach(k => delete overrides[k])
  ;(settings.value?.questions || []).forEach(q => {
    overrides[q.key] = { show: null, required: null }
  })
  ;(list || []).forEach(o => {
    if (overrides[o.key])
      overrides[o.key] = { show: o.show ?? null, required: o.required ?? null }
  })
}

const load = async () => {
  if (!props.groupId)
    return
  loading.value = true
  error.value = ''
  savedMessage.value = ''
  try {
    const { data } = await axios.get(`/application/admin/groups/${props.groupId}`)
    settings.value = data
    resetOverrides(data.overrides)
  } catch (e) {
    error.value = e?.response?.data?.error || 'Could not load this group\'s application'
  } finally {
    loading.value = false
  }
}

watch(() => props.modelValue, open => {
  if (open)
    load()
})

// What the path gives each question (no group changes), from the server.
const inherited = computed(() => settings.value?.inherited || {})

const yesNo = v => (v === true ? 'Yes' : v === false ? 'No' : '?')

const inheritLabel = (q, field) => {
  const v = inherited.value[q.key]?.[field]

  return v === null || v === undefined ? 'Inherit' : `Inherit (${yesNo(v)})`
}

const choicesFor = (q, field) => CHOICES.map(c => (c.value === null ? { ...c, title: inheritLabel(q, field) } : c))

const groups = computed(() => STEP_ORDER.map(step => ({
  step,
  title: STEP_TITLES[step],
  questions: (settings.value?.questions || []).filter(q => q.step === step),
})).filter(g => g.questions.length))

const overriddenCount = computed(() => Object.values(overrides).filter(o => o.show !== null || o.required !== null).length)

const entries = () => Object.entries(overrides)
  .filter(([, o]) => o.show !== null || o.required !== null)
  .map(([key, o]) => ({ key, show: o.show, required: o.required }))

const save = async () => {
  saving.value = true
  error.value = ''
  try {
    const { data } = await axios.put(`/application/admin/groups/${props.groupId}`, { questions: entries() })
    settings.value = data
    resetOverrides(data.overrides)
    savedMessage.value = 'Saved. New applications through this group\'s page use these settings.'
  } catch (e) {
    error.value = e?.response?.data?.error || 'Could not save'
  } finally {
    saving.value = false
  }
}

const previewOpen = ref(false)
const previewRequest = ref(null)

const preview = () => {
  previewRequest.value = { groupId: props.groupId, groupOverrides: entries() }
  previewOpen.value = true
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="760"
    scrollable
    @update:model-value="v => emit('update:modelValue', v)"
  >
    <VCard>
      <VCardTitle class="d-flex align-center pt-4">
        <span class="text-h6">Application questions<template v-if="settings && settings.group">: {{ settings.group.name }}</template></span>
        <VSpacer />
        <VBtn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="emit('update:modelValue', false)"
        />
      </VCardTitle>
      <VCardSubtitle
        v-if="settings && settings.pathLabel"
        class="group-app__sub"
      >
        Inherits {{ settings.pathLabel }} (Admin → Applications).
        {{ overriddenCount ? `${overriddenCount} ${overriddenCount === 1 ? 'question is' : 'questions are'} set for this group.` : 'Nothing is set for this group yet.' }}
      </VCardSubtitle>
      <VCardText class="group-app__body">
        <VProgressLinear
          v-if="loading"
          indeterminate
          color="primary"
        />
        <VAlert
          v-if="error"
          type="error"
          variant="tonal"
          class="mb-3"
        >
          {{ error }}
        </VAlert>
        <VAlert
          v-if="savedMessage"
          type="success"
          variant="tonal"
          class="mb-3"
        >
          {{ savedMessage }}
        </VAlert>
        <template v-if="settings && !loading">
          <section
            v-for="g in groups"
            :key="g.step"
            class="group-app__screen"
          >
            <h3 class="group-app__screen-title">
              {{ g.title }}
            </h3>
            <div
              v-for="q in g.questions"
              :key="q.key"
              class="group-app__row"
              :class="{ 'group-app__row--own': overrides[q.key] && (overrides[q.key].show !== null || overrides[q.key].required !== null) }"
            >
              <div class="group-app__q">
                {{ q.label }}
              </div>
              <template v-if="q.locked">
                <span class="group-app__always">Always asked, required</span>
              </template>
              <template v-else>
                <VSelect
                  v-model="overrides[q.key].show"
                  :items="choicesFor(q, 'show')"
                  label="Show"
                  density="compact"
                  hide-details
                  class="group-app__select"
                />
                <VSelect
                  v-model="overrides[q.key].required"
                  :items="choicesFor(q, 'required')"
                  label="Required"
                  density="compact"
                  hide-details
                  class="group-app__select"
                />
              </template>
            </div>
          </section>
        </template>
      </VCardText>
      <VCardActions>
        <VBtn
          variant="text"
          :disabled="!settings"
          @click="preview"
        >
          Preview
        </VBtn>
        <VSpacer />
        <VBtn
          color="primary"
          variant="flat"
          :loading="saving"
          :disabled="!settings"
          @click="save"
        >
          Save
        </VBtn>
      </VCardActions>
    </VCard>
    <ApplicationPreviewDialog
      v-model="previewOpen"
      :request="previewRequest"
      :title="`Preview: ${settings && settings.group ? settings.group.name : ''}`"
    />
  </VDialog>
</template>

<style lang="scss" scoped>
.group-app__sub {
  white-space: normal;
}

.group-app__body {
  max-height: 70vh;
}

.group-app__screen {
  margin-bottom: 16px;
}

.group-app__screen-title {
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--ds-text-muted);
}

.group-app__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 8px;
  border-bottom: thin solid var(--ds-border-light);
}

.group-app__row--own {
  border-left: 3px solid var(--ds-brand-primary-dark);
}

.group-app__q {
  flex: 1;
  min-width: 0;
  font-size: 16px;
  color: var(--ds-text-strong);
}

.group-app__always {
  font-size: 14px;
  color: var(--ds-text-muted);
}

.group-app__select {
  flex: 0 0 190px;
}

@media (max-width: 600px) {
  .group-app__row {
    flex-wrap: wrap;
  }

  .group-app__q {
    flex-basis: 100%;
  }
}
</style>
